import { dataService } from './data-service';
import type { Asset, Transaction } from '@/types/asset';
import { getAssetPrimaryPhotoUrl } from '@/lib/asset-utils';
import type { LucideIcon } from 'lucide-react';

export type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline';

export interface SearchResult {
  id: string;
  type: 'asset' | 'transaction' | 'feature';
  title: string;
  subtitle: string;
  metadata?: string;
  badge?: string;
  badgeVariant?: BadgeVariant;
  imageUrl?: string;
  route: string;
  iconName?: string; // Icon name to be imported in the component
}

// Define searchable features/screens
interface FeatureScreen {
  id: string;
  title: string;
  subtitle: string;
  route: string;
  keywords: string[];
  iconName: string; // Lucide icon name
}

const featureScreens: FeatureScreen[] = [
  { id: 'dashboard', title: 'Dashboard', subtitle: 'Overview and metrics', route: '/dashboard', keywords: ['dashboard', 'home', 'overview', 'metrics'], iconName: 'LayoutDashboard' },
  { id: 'assets', title: 'Assets', subtitle: 'Asset management', route: '/assets', keywords: ['assets', 'equipment', 'inventory'], iconName: 'Package' },
  { id: 'tasks', title: 'Tasks', subtitle: 'Task management', route: '/tasks', keywords: ['tasks', 'work items', 'todos'], iconName: 'CheckSquare' },
  { id: 'audit-active', title: 'Active Audits', subtitle: 'Audits • Currently in progress', route: '/audits/active', keywords: ['audit', 'audits', 'active', 'in progress'], iconName: 'ClipboardCheck' },
  { id: 'audit-scheduled', title: 'Scheduled Audits', subtitle: 'Audits • Upcoming audits', route: '/audits/scheduled', keywords: ['audit', 'audits', 'scheduled', 'upcoming'], iconName: 'ClipboardCheck' },
  { id: 'audit-history', title: 'Audit History', subtitle: 'Audits • Completed audits', route: '/audits/history', keywords: ['audit', 'audits', 'history', 'completed', 'past'], iconName: 'FileText' },
  { id: 'audit-exceptions', title: 'Audit Exceptions', subtitle: 'Audits • Issues and discrepancies', route: '/audits/exceptions', keywords: ['audit', 'audits', 'exceptions', 'issues', 'discrepancies'], iconName: 'Shield' },
  { id: 'audit-create', title: 'Create Audit', subtitle: 'Audits • Start a new audit', route: '/audits/create', keywords: ['audit', 'audits', 'create', 'new'], iconName: 'PlusCircle' },
  { id: 'additions', title: 'Additions', subtitle: 'Transactions • Asset additions', route: '/transactions/additions', keywords: ['transactions', 'additions', 'add', 'new assets'], iconName: 'PlusCircle' },
  { id: 'depreciation', title: 'Depreciation', subtitle: 'Transactions • Asset depreciation', route: '/transactions/depreciation', keywords: ['transactions', 'depreciation', 'value'], iconName: 'TrendingDown' },
  { id: 'revaluation', title: 'Revaluation', subtitle: 'Transactions • Asset revaluation', route: '/transactions/revaluation', keywords: ['transactions', 'revaluation', 'value'], iconName: 'RefreshCw' },
  { id: 'insights', title: 'Insights', subtitle: 'Reports • Analytics and insights', route: '/insights', keywords: ['reports', 'insights', 'analytics', 'charts'], iconName: 'Sparkles' },
  { id: 'enquiry', title: 'Transaction History', subtitle: 'Reports • Transaction enquiry', route: '/enquiry', keywords: ['reports', 'transactions', 'history', 'enquiry'], iconName: 'Search' },
  { id: 'audit-trail', title: 'Audit Trail', subtitle: 'Reports • System audit trail', route: '/audit', keywords: ['reports', 'audit trail', 'logs', 'history'], iconName: 'FileText' },
  { id: 'checkinout', title: 'Check-In / Check-Out', subtitle: 'Management • Asset tracking', route: '/checkinout', keywords: ['check in', 'check out', 'checkout', 'checkin', 'tracking'], iconName: 'Package' },
  { id: 'compliance', title: 'Compliance', subtitle: 'Management • Compliance tracking', route: '/compliance', keywords: ['compliance', 'regulations'], iconName: 'Shield' },
  { id: 'locations', title: 'Locations', subtitle: 'Configuration • Manage locations', route: '/administration/locations', keywords: ['locations', 'sites', 'places', 'configuration'], iconName: 'MapPin' },
  { id: 'categories', title: 'Categories', subtitle: 'Configuration • Asset categories', route: '/administration/categories', keywords: ['categories', 'types', 'classification', 'configuration'], iconName: 'FolderTree' },
  { id: 'equipment', title: 'Equipment', subtitle: 'Configuration • Equipment types', route: '/administration/equipment', keywords: ['equipment', 'types', 'configuration'], iconName: 'Wrench' },
  { id: 'disposal-reasons', title: 'Disposal Reasons', subtitle: 'Configuration • Disposal reasons', route: '/administration/disposal-reasons', keywords: ['disposal', 'reasons', 'configuration'], iconName: 'Trash2' },
  { id: 'import-assets', title: 'Import Assets', subtitle: 'Data Management • Import assets', route: '/administration/imports', keywords: ['import', 'assets', 'upload', 'data'], iconName: 'Upload' },
  { id: 'import-transactions', title: 'Import Transactions', subtitle: 'Data Management • Import transactions', route: '/administration/import-transactions', keywords: ['import', 'transactions', 'upload', 'data'], iconName: 'Upload' },
  { id: 'team-settings', title: 'Team Settings', subtitle: 'Settings • Teams and permissions', route: '/administration/teams-permissions', keywords: ['teams', 'permissions', 'users', 'settings'], iconName: 'Users' },
  { id: 'feature-settings', title: 'Feature Settings', subtitle: 'Settings • Module configuration', route: '/settings/features', keywords: ['features', 'settings', 'modules', 'configuration'], iconName: 'Sliders' },
  { id: 'asset-options', title: 'Asset Options', subtitle: 'Settings • Asset configuration', route: '/settings/asset-options', keywords: ['asset', 'options', 'settings', 'configuration'], iconName: 'Settings' },
  { id: 'help', title: 'Help & Support', subtitle: 'Documentation and support', route: '/help', keywords: ['help', 'support', 'documentation', 'guide'], iconName: 'HelpCircle' },
];

class SearchService {
  async search(query: string): Promise<SearchResult[]> {
    if (!query.trim()) {
      return [];
    }

    try {
      // Get ALL assets and transactions, then filter locally
      // This ensures search results match what's shown on the Assets page
      const [assetsResponse, transactionsResponse] = await Promise.all([
        dataService.getAssets({ pageSize: 10000 }),
        dataService.getTransactions({ pageSize: 10000 }),
      ]);

      const results: SearchResult[] = [];
      const queryLower = query.toLowerCase();

            // Filter assets locally (same logic as Assets page)
            const filteredAssets = assetsResponse.data.filter((asset: Asset) =>
              asset.name.toLowerCase().includes(queryLower) ||
              asset.code.toLowerCase().includes(queryLower) ||
              asset.serialNumber?.toLowerCase().includes(queryLower) ||
              asset.description?.toLowerCase().includes(queryLower) ||
              asset.category?.toLowerCase().includes(queryLower) ||
              asset.location?.toLowerCase().includes(queryLower) ||
              asset.manufacturer?.toLowerCase().includes(queryLower) ||
              asset.model?.toLowerCase().includes(queryLower)
            );

            // Map asset results (limit to 5)
            const assetResults = filteredAssets.slice(0, 5).map((asset: Asset) => ({
        id: `asset-${asset.id}`,
        type: 'asset' as const,
                title: asset.name ?? 'Unnamed Asset',
                subtitle: `${asset.code ?? 'N/A'} • ${asset.category ?? 'Uncategorized'}`,
        metadata: asset.location ?? '',
        badge: asset.status ? asset.status.charAt(0).toUpperCase() + asset.status.slice(1) : 'Unknown',
        badgeVariant: this.getAssetStatusVariant(asset.status),
        imageUrl: getAssetPrimaryPhotoUrl(asset),
        route: `/assets?id=${asset.id}`,
        assetData: asset, // Include full asset data for modal display
      }));

            // Filter and map transaction results (limit to 5)
            const filteredTransactions = transactionsResponse.data.filter(
              (txn: Transaction) =>
                txn.assetId?.toLowerCase().includes(queryLower) ||
                txn.assetName?.toLowerCase().includes(queryLower) ||
                txn.type?.toLowerCase().includes(queryLower) ||
                txn.batchId?.toLowerCase().includes(queryLower)
            );

            const transactionResults = filteredTransactions.slice(0, 5).map((txn: Transaction) => ({
        id: `transaction-${txn.id}`,
        type: 'transaction' as const,
                title: txn.assetName ?? 'Unnamed Transaction',
                subtitle: `${txn.auditNumber ?? txn.assetId ?? 'N/A'} • ${txn.type ?? 'Unknown'}`,
        metadata: `${txn.currency ?? ''} ${txn.amount?.toLocaleString() ?? '0'} • ${new Date(txn.transactionDate).toLocaleDateString('en-GB')}`,
        badge: txn.status ? txn.status.charAt(0).toUpperCase() + txn.status.slice(1) : 'Unknown',
        badgeVariant: this.getTransactionStatusVariant(txn.status),
        route: `/enquiry?transactionId=${txn.auditNumber || txn.id}`,
      }));

      // Search features/screens
      const filteredFeatures = featureScreens.filter(feature =>
        feature.title.toLowerCase().includes(queryLower) ||
        feature.subtitle.toLowerCase().includes(queryLower) ||
        feature.keywords.some(keyword => keyword.toLowerCase().includes(queryLower))
      );

      const featureResults = filteredFeatures.slice(0, 5).map(feature => ({
        id: `feature-${feature.id}`,
        type: 'feature' as const,
        title: feature.title,
        subtitle: feature.subtitle,
        route: feature.route,
        iconName: feature.iconName,
      }));

      results.push(...assetResults, ...transactionResults, ...featureResults);
      return results;
    } catch (error) {
      console.error('Search error:', error);
      throw error;
    }
  }

  private getAssetStatusVariant(status: string): BadgeVariant {
    switch (status?.toLowerCase()) {
      case 'active':
        return 'default';
      case 'maintenance':
        return 'secondary';
      case 'disposed':
        return 'destructive';
      default:
        return 'outline';
    }
  }

  private getTransactionStatusVariant(status: string): BadgeVariant {
    switch (status?.toLowerCase()) {
      case 'posted':
        return 'default';
      case 'approved':
        return 'secondary';
      case 'draft':
        return 'outline';
      default:
        return 'outline';
    }
  }
}

export const searchService = new SearchService();
